import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { AppNotification } from '../types/notification';
import {
  getNotifications,
  markAsRead as markAsReadService,
  markAllAsRead as markAllAsReadService,
  clearNotifications as clearService,
  deleteNotification as deleteService,
} from '../services/inAppNotification';

interface NotificationContextType {
  notifications: AppNotification[];
  unreadCount: number;
  isOpen: boolean;
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  deleteOne: (id: string) => void;
  clearAll: () => void;
  refresh: (options?: { force?: boolean }) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

function areNotificationsEqual(a: AppNotification[], b: AppNotification[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((notification, index) => {
    const other = b[index];
    return Boolean(other) &&
      notification.id === other.id &&
      notification.read === other.read &&
      notification.createdAt === other.createdAt &&
      notification.title === other.title &&
      notification.message === other.message;
  });
}

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  // Timestamp-based skip: suppress polling until this time (ms) has passed
  // This gives the backend time to persist all changes before we re-fetch
  const skipUntilRef = useRef(0);

  // Use user.email as the notification key (matches how notifications are stored)
  const userId = user?.email || '';

  // ALWAYS derive unreadCount from the notifications array
  // This guarantees the badge is in sync with what's displayed
  const unreadCount = useMemo(() => {
    const count = notifications.filter((n) => !n.read).length;
    // Update browser tab title
    if (count > 0) {
      document.title = `(${count}) Creyente One`;
    } else {
      document.title = 'Creyente One';
    }
    return count;
  }, [notifications]);

  const refresh = useCallback(async (options: { force?: boolean } = {}) => {
    if (!userId) return;
    // Skip this refresh cycle if we're within the skip window
    if (!options.force && Date.now() < skipUntilRef.current) {
      return;
    }
    const all = await getNotifications(userId, options);
    setNotifications((prev) => areNotificationsEqual(prev, all) ? prev : all);
  }, [userId]);

  // Poll for new notifications every 30 seconds
  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 30000);
    return () => clearInterval(interval);
  }, [refresh]);

  const markAsRead = async (id: string) => {
    if (!userId) return;
    // Check if the notification is already read to avoid double-decrementing
    const notif = notifications.find((n) => n.id === id);
    if (notif?.read) return; // Already read, nothing to do
    // Optimistically update local state immediately
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
    // Persist to backend, then suppress polling for 10s to avoid overwriting
    await markAsReadService(userId, id);
    skipUntilRef.current = Date.now() + 10000;
  };

  const markAllAsRead = async () => {
    if (!userId) return;
    // Optimistically update local state
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    // Suppress polling while backend processes all marks (can take several seconds)
    skipUntilRef.current = Date.now() + 15000;
    // Persist to backend (will fall back to individual marks if bulk fails)
    await markAllAsReadService(userId);
    // After backend is done, allow the NEXT poll to refresh with confirmed data
    skipUntilRef.current = Date.now() + 2000;
  };

  const deleteOne = async (id: string) => {
    if (!userId) return;
    // Optimistically remove from local state so the row disappears immediately
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    // Suppress polling briefly so a stale fetch can't resurrect the deleted row
    skipUntilRef.current = Date.now() + 10000;
    await deleteService(userId, id);
  };

  const clearAll = async () => {
    if (!userId) return;
    // Optimistically clear so the panel empties instantly
    setNotifications([]);
    // Hold off polling while the backend processes the deletes
    skipUntilRef.current = Date.now() + 10000;
    await clearService(userId);
    // Confirm against the backend
    await refresh({ force: true });
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        isOpen,
        openPanel: () => setIsOpen(true),
        closePanel: () => setIsOpen(false),
        togglePanel: () => setIsOpen((p) => !p),
        markAsRead,
        markAllAsRead,
        deleteOne,
        clearAll,
        refresh,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) throw new Error('useNotifications must be used within NotificationProvider');
  return context;
};

